<template>
  <div class="modal-overlay" @mousedown.self="onOverlayMouseDown" @mouseup.self="onOverlayMouseUp">
    <div class="modal-container">
      <div class="modal-header">
        <button
          type="button"
          class="modal-close-button"
          @click="close"
          aria-label="Close authentication"
        >
          &times;
        </button>
        <h3>{{ isLogin ? 'Log in' : 'Sign up' }}</h3>
      </div>

      <div class="modal-body">
        <div v-if="!isForgotPassword">
          <div class="tabs">
            <button 
              :class="['tab-btn', { active: isLogin }]" 
              @click="isLogin = true; resetMessages()"
            >
              Log In
            </button>
            <button
              :class="['tab-btn', { active: !isLogin }]"
              @click="emit('open-signup')"
            >
              Sign Up
            </button>
          </div>

          <form @submit.prevent="handleSubmit" class="auth-form">
            <div class="form-group">
              <label for="email">Email</label>
              <input 
                id="email" 
                v-model="email" 
                type="email" 
                required 
                placeholder="you@example.com"
                autocomplete="email"
              />
            </div>

            <div class="form-group">
              <label for="password">Password</label>
              <div class="password-input">
                <input 
                  id="password" 
                  v-model="password" 
                  :type="getPasswordType(isPasswordVisible)" 
                  required 
                  placeholder="••••••••"
                  :minlength="PASSWORD_MIN_LENGTH"
                  autocomplete="current-password"
                />
                <span
                  class="toggle-visibility-btn"
                  role="button"
                  tabindex="0"
                  @click="togglePasswordVisibility"
                  @keydown.enter.prevent="togglePasswordVisibility"
                  @keydown.space.prevent="togglePasswordVisibility"
                  :aria-label="isPasswordVisible ? 'Hide password' : 'Show password'"
                >
                  <svg
                    v-if="isPasswordVisible"
                    class="toggle-visibility-icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.5 3.5a1 1 0 0 1 1.4 0l3.1 3.1c1.2-.5 2.5-.8 3.9-.8 5.2 0 9.4 3.7 11 6.2a1.6 1.6 0 0 1 0 1.8 15.2 15.2 0 0 1-4.1 4.2l2.7 2.7a1 1 0 1 1-1.4 1.4l-17-17a1 1 0 0 1 0-1.4Zm7.8 7.8a2.5 2.5 0 0 0 3.4 3.4l-3.4-3.4Zm-2.5-2.5 2.1 2.1a4.5 4.5 0 0 1 6 6l2.2 2.2a13 13 0 0 0 3.7-3.7c-1.4-2.2-4.9-5.6-9.9-5.6-1.4 0-2.8.2-4.1.6Zm4.1 10.3c-5 0-8.5-3.4-9.9-5.6a13 13 0 0 1 3.2-3.5l1.4 1.4a4.5 4.5 0 0 0 5.8 5.8l1.7 1.7c-.7.1-1.5.2-2.2.2Z"
                    />
                  </svg>
                  <svg
                    v-else
                    class="toggle-visibility-icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 5.8c5.2 0 9.4 3.7 11 6.2a1.6 1.6 0 0 1 0 1.8c-1.6 2.5-5.8 6.2-11 6.2s-9.4-3.7-11-6.2a1.6 1.6 0 0 1 0-1.8c1.6-2.5 5.8-6.2 11-6.2Zm0 2c-3.9 0-7.3 2.6-8.8 4.6 1.5 2 4.9 4.6 8.8 4.6s7.3-2.6 8.8-4.6c-1.5-2-4.9-4.6-8.8-4.6Zm0 1.7a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"
                    />
                  </svg>
                </span>
              </div>
               <p v-if="!isLogin" class="hint">Must be at least {{ PASSWORD_MIN_LENGTH }} characters, with uppercase, lowercase, and numbers.</p>
            </div>

            <button
              v-if="isLogin"
              type="button"
              class="link-btn"
              @click="startForgotPassword"
              aria-label="Forgot password"
            >
              Forgot password?
            </button>

            <div v-if="authStore.error" class="error-message">
              {{ authStore.error }}
            </div>

            <div v-if="successMessage" class="success-message">
              {{ successMessage }}
            </div>

            <button 
              type="submit" 
              class="submit-btn" 
              :disabled="authStore.isLoading"
            >
              <span v-if="authStore.isLoading">Processing...</span>
              <span v-else>{{ isLogin ? 'Log In' : 'Create Account' }}</span>
            </button>
          </form>
        </div>

        <div v-else>
          <h4 class="forgot-title">Reset password</h4>
          <form @submit.prevent="handleForgotPasswordSubmit" class="auth-form">
            <div class="form-group">
              <label for="reset-email">Email</label>
              <input 
                id="reset-email" 
                v-model="email" 
                type="email" 
                required 
                placeholder="you@example.com"
                autocomplete="email"
              />
            </div>

            <div v-if="resetStep === RESET_STEP_CONFIRM">
              <div class="form-group">
                <label for="reset-code">Reset Code</label>
                <input 
                  id="reset-code" 
                  v-model="resetCode" 
                  type="text" 
                  required 
                  placeholder="123456"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                />
              </div>

              <div class="form-group">
                <label for="new-password">New Password</label>
                <div class="password-input">
                  <input 
                    id="new-password" 
                    v-model="newPassword" 
                    :type="getPasswordType(isResetPasswordVisible)" 
                    required 
                    placeholder="••••••••"
                    :minlength="PASSWORD_MIN_LENGTH"
                    autocomplete="new-password"
                  />
                  <span
                    class="toggle-visibility-btn"
                    role="button"
                    tabindex="0"
                    @click="toggleResetPasswordVisibility"
                    @keydown.enter.prevent="toggleResetPasswordVisibility"
                    @keydown.space.prevent="toggleResetPasswordVisibility"
                    :aria-label="isResetPasswordVisible ? 'Hide new password' : 'Show new password'"
                  >
                    <svg
                      v-if="isResetPasswordVisible"
                      class="toggle-visibility-icon"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        d="M3.5 3.5a1 1 0 0 1 1.4 0l3.1 3.1c1.2-.5 2.5-.8 3.9-.8 5.2 0 9.4 3.7 11 6.2a1.6 1.6 0 0 1 0 1.8 15.2 15.2 0 0 1-4.1 4.2l2.7 2.7a1 1 0 1 1-1.4 1.4l-17-17a1 1 0 0 1 0-1.4Zm7.8 7.8a2.5 2.5 0 0 0 3.4 3.4l-3.4-3.4Zm-2.5-2.5 2.1 2.1a4.5 4.5 0 0 1 6 6l2.2 2.2a13 13 0 0 0 3.7-3.7c-1.4-2.2-4.9-5.6-9.9-5.6-1.4 0-2.8.2-4.1.6Zm4.1 10.3c-5 0-8.5-3.4-9.9-5.6a13 13 0 0 1 3.2-3.5l1.4 1.4a4.5 4.5 0 0 0 5.8 5.8l1.7 1.7c-.7.1-1.5.2-2.2.2Z"
                      />
                    </svg>
                    <svg
                      v-else
                      class="toggle-visibility-icon"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 5.8c5.2 0 9.4 3.7 11 6.2a1.6 1.6 0 0 1 0 1.8c-1.6 2.5-5.8 6.2-11 6.2s-9.4-3.7-11-6.2a1.6 1.6 0 0 1 0-1.8c1.6-2.5 5.8-6.2 11-6.2Zm0 2c-3.9 0-7.3 2.6-8.8 4.6 1.5 2 4.9 4.6 8.8 4.6s7.3-2.6 8.8-4.6c-1.5-2-4.9-4.6-8.8-4.6Zm0 1.7a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z"
                      />
                    </svg>
                  </span>
                </div>
              </div>
            </div>

            <div v-if="authStore.error" class="error-message">
              {{ authStore.error }}
            </div>

            <div v-if="successMessage" class="success-message">
              {{ successMessage }}
            </div>

            <button 
              type="submit" 
              class="submit-btn" 
              :disabled="authStore.isLoading"
            >
              <span v-if="authStore.isLoading">Processing...</span>
              <span v-else>{{ resetStep === RESET_STEP_REQUEST ? 'Send Reset Code' : 'Update Password' }}</span>
            </button>

            <button
              type="button"
              class="link-btn"
              @click="cancelForgotPassword"
              aria-label="Back to login"
            >
              Back to login
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/store/auth.store'

const emit = defineEmits(['close', 'authenticated', 'verification-required', 'open-signup'])

const authStore = useAuthStore()
const PASSWORD_MIN_LENGTH = 8
const PASSWORD_INPUT_TYPE = 'password'
const TEXT_INPUT_TYPE = 'text'
const RESET_STEP_REQUEST = 'request'
const RESET_STEP_CONFIRM = 'confirm'
type ResetStep = typeof RESET_STEP_REQUEST | typeof RESET_STEP_CONFIRM

const isLogin = ref(true)
const isForgotPassword = ref(false)
const resetStep = ref<ResetStep>(RESET_STEP_REQUEST)
const isPasswordVisible = ref(false)
const isResetPasswordVisible = ref(false)
const email = ref('')
const password = ref('')
const resetCode = ref('')
const newPassword = ref('')
const successMessage = ref('')

const overlayMouseDownFired = ref(false)

const onOverlayMouseDown = () => {
  overlayMouseDownFired.value = true
}

const onOverlayMouseUp = () => {
  if (overlayMouseDownFired.value) {
    emit('close')
  }
  overlayMouseDownFired.value = false
}

const resetOverlayFlag = () => {
  overlayMouseDownFired.value = false
}

onMounted(() => {
  document.addEventListener('mouseup', resetOverlayFlag)
})

onUnmounted(() => {
  document.removeEventListener('mouseup', resetOverlayFlag)
})

const close = () => {
  emit('close')
}

const getPasswordType = (isVisible: boolean) => (isVisible ? TEXT_INPUT_TYPE : PASSWORD_INPUT_TYPE)

const togglePasswordVisibility = () => {
  isPasswordVisible.value = !isPasswordVisible.value
}

const toggleResetPasswordVisibility = () => {
  isResetPasswordVisible.value = !isResetPasswordVisible.value
}

const resetMessages = () => {
  authStore.error = null
  successMessage.value = ''
  isPasswordVisible.value = false
}

const startForgotPassword = () => {
  isForgotPassword.value = true
  resetStep.value = RESET_STEP_REQUEST
  isResetPasswordVisible.value = false
  resetCode.value = ''
  newPassword.value = ''
  resetMessages()
}

const cancelForgotPassword = () => {
  isForgotPassword.value = false
  resetStep.value = RESET_STEP_REQUEST
  isResetPasswordVisible.value = false
  resetMessages()
}

const handleSubmit = async () => {
  resetMessages()

  try {
    await authStore.login(email.value, password.value)
    if (authStore.isEmailVerified) {
      emit('authenticated')
      close()
    } else {
      emit('verification-required', { email: email.value, password: password.value })
    }
  } catch (loginError) {
    const errorName =
      loginError instanceof Error
        ? loginError.name
        : typeof loginError === 'object' && loginError !== null && 'name' in loginError
          ? String((loginError as { name?: unknown }).name ?? '')
          : ''
    if (errorName === 'UserNotConfirmedException') {
      emit('verification-required', { email: email.value, password: password.value })
    }
    console.error('Login failed', loginError)
  }
}

const handleForgotPasswordSubmit = async () => {
  resetMessages()
  const username = email.value.trim()
  if (!username) {
    authStore.error = 'Email is required to reset your password.'
    return
  }

  try {
    if (resetStep.value === RESET_STEP_REQUEST) {
      const result = await authStore.requestPasswordReset(username)
      const nextStep = result?.nextStep?.resetPasswordStep ?? ''
      if (nextStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        resetStep.value = RESET_STEP_CONFIRM
        successMessage.value = 'Reset code sent. Please check your email.'
        return
      }
      if (result?.isPasswordReset) {
        successMessage.value = 'Password reset completed. Please log in.'
        isForgotPassword.value = false
        isLogin.value = true
        return
      }
      resetStep.value = RESET_STEP_CONFIRM
      successMessage.value = 'If an account exists, a reset code has been sent.'
      return
    }

    const trimmedCode = resetCode.value.trim()
    const trimmedPassword = newPassword.value.trim()
    if (!trimmedCode || !trimmedPassword) {
      authStore.error = 'Reset code and new password are required.'
      return
    }
    if (trimmedPassword.length < PASSWORD_MIN_LENGTH) {
      authStore.error = `New password must be at least ${PASSWORD_MIN_LENGTH} characters.`
      return
    }

    await authStore.confirmPasswordReset(username, trimmedCode, trimmedPassword)
    successMessage.value = 'Password updated. You can log in now.'
    isForgotPassword.value = false
    isLogin.value = true
    isResetPasswordVisible.value = false
    password.value = ''
    resetCode.value = ''
    newPassword.value = ''
  } catch (error) {
    console.error('Password reset flow failed', error)
  }
}
</script>

<style scoped>
.modal-overlay {
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.tabs {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  background: transparent;
  border: none;
  padding: 0;
}

.tab-btn {
  flex: 1;
  background: transparent;
  border: 1px solid var(--ui-inverse-cta-bg);
  color: var(--ui-inverse-text);
  padding: 0.6rem 1.25rem;
  cursor: pointer;
  font-weight: 700;
  border-radius: 999px;
  width: auto;
  height: auto;
  box-shadow: none;
  font-family: var(--ui-inverse-font-family);
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.tab-btn:hover {
  background: rgba(var(--vt-c-accent-rgb), 0.12);
}

.tab-btn.active {
  background: var(--ui-inverse-cta-bg);
  color: var(--ui-inverse-cta-text);
  border-color: var(--ui-inverse-cta-bg);
}

.tab-btn.active:hover {
  background: var(--ui-inverse-cta-bg-hover);
  border-color: var(--ui-inverse-cta-bg-hover);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-size: 0.9rem;
  color: var(--ui-inverse-text);
  font-weight: 600;
}

.form-group input {
  padding: 0.75rem 1rem;
  background: var(--ui-inverse-control-bg);
  border: 1px solid var(--ui-inverse-control-border);
  border-radius: 999px;
  color: var(--ui-inverse-control-text);
  font-family: var(--ui-inverse-font-family);
}

.submit-btn {
  width: 100%;
  height: auto;
  margin-top: 1rem;
  padding: 0.875rem;
  background: var(--vt-c-accent);
  color: var(--color-background);
  font-weight: 700;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.submit-btn:hover {
  background: var(--ui-inverse-cta-bg-hover);
  transform: translateY(-1px);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.password-input {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input input {
  padding-right: 2.75rem;
  width: 100%;
}

.toggle-visibility-btn {
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: var(--ui-inverse-control-placeholder);
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: auto;
  box-shadow: none;
}

.toggle-visibility-icon {
  width: 1.1rem;
  height: 1.1rem;
  fill: currentColor;
}

.link-btn {
  background: none;
  border: none;
  color: var(--ui-inverse-text);
  cursor: pointer;
  padding: 0;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  text-align: left;
  width: auto;
  height: auto;
  box-shadow: none;
}

.link-btn:hover {
  color: var(--ui-inverse-text);
}

.forgot-title {
  margin: 0 0 1rem;
  font-size: 1.1rem;
  color: var(--ui-inverse-text);
  font-weight: 700;
}

.error-message {
  color: #8f1434;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  background: rgba(143, 20, 52, 0.08);
  padding: 0.75rem;
  border-radius: 6px;
}

.success-message {
  color: var(--ui-inverse-text);
  margin-bottom: 1rem;
  font-size: 0.9rem;
  background: rgba(16, 26, 52, 0.1);
  padding: 0.75rem; border-radius: 6px;
}

.hint {
  font-size: 0.8rem;
  color: var(--ui-inverse-text-muted);
  margin-top: 0.25rem;
}
</style>
